import { Button, Input, Label, SecretInput, Spinner } from '@repo/ui'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { AuthShell } from '@/components/auth/auth-shell'
import { useAuth } from '@/hooks/use-auth'
import { api, authApi } from '@/lib/api'

export const Route = createFileRoute('/aceitar-convite')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: AcceptInvitePage,
})

type InvitationPreview = {
  id: string
  email: string
  organizationName: string
  inviterName: string
  userExists: boolean
  expiresAt: string
}

function AcceptInvitePage() {
  const navigate = useNavigate()
  const { token } = useSearch({ from: '/aceitar-convite' })
  const { refresh, signOut, user, loading: authLoading } = useAuth()
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null)
  const [loadingInvite, setLoadingInvite] = useState(true)
  const [inviteInvalid, setInviteInvalid] = useState(false)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadingInvite(false)
      setInviteInvalid(true)
      return
    }

    let cancelled = false

    async function loadInvitation() {
      setLoadingInvite(true)
      try {
        const { data } = await api.get<InvitationPreview>(
          `/invitations/${token}`
        )
        if (cancelled) return
        setInvitation(data)
        setInviteInvalid(false)
      } catch {
        if (cancelled) return
        setInviteInvalid(true)
      } finally {
        if (!cancelled) setLoadingInvite(false)
      }
    }

    void loadInvitation()

    return () => {
      cancelled = true
    }
  }, [token])

  async function acceptInvitation() {
    await authApi.post('/api/auth/organization/accept-invitation', {
      invitationId: token,
    })
    await refresh()
    toast.success('Convite aceito! Bem-vindo ao AgroSDR!')
    await navigate({ to: '/inbox' })
  }

  async function handleCreateAccount(event: FormEvent) {
    event.preventDefault()
    if (!invitation) return

    if (password.length < 8) {
      toast.error('A senha deve ter no mínimo 8 caracteres')
      return
    }

    if (password !== confirm) {
      toast.error('As senhas não conferem')
      return
    }

    setSubmitting(true)

    try {
      await authApi.post('/api/auth/sign-up/email', {
        email: invitation.email,
        password,
        name,
      })
      await acceptInvitation()
    } catch {
      setSubmitting(false)
    }
  }

  async function handleLogin(event: FormEvent) {
    event.preventDefault()
    if (!invitation) return

    setSubmitting(true)

    try {
      await authApi.post('/api/auth/sign-in/email', {
        email: invitation.email,
        password,
        rememberMe: true,
      })
      await acceptInvitation()
    } catch {
      setSubmitting(false)
    }
  }

  if (authLoading || loadingInvite) {
    return (
      <AuthShell title="Aceitar convite" subtitle="Validando seu convite">
        <div className="flex justify-center py-8">
          <Spinner className="text-primary h-6 w-6" />
        </div>
      </AuthShell>
    )
  }

  if (inviteInvalid || !token || !invitation) {
    return (
      <AuthShell
        title="Convite inválido"
        subtitle="Este link de convite não é válido"
      >
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <AlertTriangle className="text-destructive h-12 w-12" />
          <p className="text-muted-foreground text-sm">
            Solicite um novo convite ao administrador da organização.
          </p>
        </div>
      </AuthShell>
    )
  }

  if (user && user.email.toLowerCase() !== invitation.email.toLowerCase()) {
    return (
      <AuthShell
        title="Conta diferente"
        subtitle={`Este convite foi enviado para ${invitation.email}`}
      >
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Você está logado como {user.email}. Saia desta conta para aceitar o
            convite com o e-mail correto.
          </p>
          <Button
            className="w-full"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true)
              await signOut()
              setSubmitting(false)
            }}
          >
            {submitting ? <Spinner /> : null}
            Sair desta conta
          </Button>
        </div>
      </AuthShell>
    )
  }

  if (user) {
    return (
      <AuthShell
        title="Aceitar convite"
        subtitle={`${invitation.inviterName} convidou você para ${invitation.organizationName}`}
      >
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Você está logado como {user.email}. Confirme para entrar na
            organização.
          </p>
          <Button
            className="w-full"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true)
              try {
                await acceptInvitation()
              } catch {
                setSubmitting(false)
              }
            }}
          >
            {submitting ? <Spinner /> : null}
            Aceitar convite
          </Button>
        </div>
      </AuthShell>
    )
  }

  if (invitation.userExists) {
    return (
      <AuthShell
        title="Entrar para aceitar"
        subtitle={`${invitation.inviterName} convidou você para ${invitation.organizationName}`}
      >
        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={invitation.email}
              readOnly
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password-existing">Senha</Label>
            <SecretInput
              id="password-existing"
              visibilityLabel="senha"
              required
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? <Spinner /> : null}
            Entrar e aceitar convite
          </Button>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Criar conta"
      subtitle={`${invitation.inviterName} convidou você para ${invitation.organizationName}`}
    >
      <form onSubmit={handleCreateAccount} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={invitation.email}
            readOnly
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="name">Nome completo</Label>
          <Input
            id="name"
            type="text"
            required
            autoComplete="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password-new">Senha</Label>
          <SecretInput
            id="password-new"
            visibilityLabel="senha"
            required
            autoComplete="new-password"
            placeholder="Mínimo 8 caracteres"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm">Confirmar senha</Label>
          <SecretInput
            id="confirm"
            visibilityLabel="confirmação da senha"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? <Spinner /> : null}
          Criar conta e aceitar convite
        </Button>
      </form>
    </AuthShell>
  )
}
